import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  limit as firestoreLimit,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { normalizeName } from '../../utils/text-utils';

/**
 * Search requesters for a specific client
 * @param {string} clientId - Client ID
 * @param {string} q - Search query
 * @param {number} limitN - Maximum number of results (default: 10)
 * @returns {Promise<Array>} Array of requester objects
 */
export async function searchRequesters(clientId, q = '', limitN = 10) {
  try {
    const requestersRef = collection(db, `clients/${clientId}/requesters`);
    
    if (!q.trim()) {
      // Return first limitN names ordered by nameLower
      const queryRef = query(
        requestersRef,
        orderBy('nameLower'),
        firestoreLimit(limitN)
      );
      
      const snapshot = await getDocs(queryRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    // Search with prefix matching
    const qNorm = normalizeName(q);
    const queryRef = query(
      requestersRef,
      orderBy('nameLower'),
      startAt(qNorm),
      endAt(qNorm + '\uf8ff'),
      firestoreLimit(limitN)
    );
    
    const snapshot = await getDocs(queryRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error searching requesters:', error);
    throw error;
  }
}

/**
 * Create or get existing requester by name
 * @param {string} clientId - Client ID
 * @param {string} name - Requester name
 * @returns {Promise<Object>} Requester object
 */
export async function createOrGetRequester(clientId, name) {
  try {
    const nameLower = normalizeName(name);
    const requestersRef = collection(db, `clients/${clientId}/requesters`);
    
    // Check if requester already exists
    const existingQuery = query(
      requestersRef,
      where('nameLower', '==', nameLower)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    
    if (!existingSnapshot.empty) {
      // Return existing requester
      const existingDoc = existingSnapshot.docs[0];
      return {
        id: existingDoc.id,
        ...existingDoc.data()
      };
    }
    
    // Create new requester
    const newRequester = {
      name: name.trim(),
      nameLower,
      createdAt: new Date().toISOString()
    };
    
    const docRef = await addDoc(requestersRef, newRequester);
    
    return {
      id: docRef.id,
      ...newRequester
    };
  } catch (error) {
    console.error('Error creating/getting requester:', error);
    throw error;
  }
}

/**
 * Delete a service requester for a client.
 * @param {string} clientId
 * @param {string} requesterId
 */
export async function deleteRequester(clientId, requesterId) {
  try {
    await deleteDoc(doc(db, `clients/${clientId}/requesters`, requesterId));
  } catch (error) {
    console.error('Error deleting requester:', error);
    throw error;
  }
}